import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CreateEventsDoc } from '@minecraft.graphql-operations';
import { useMutation } from '@apollo/client';
import { captureExceptionEvent } from '@blocs.observability';
import { useUserContext } from '@blocs.auth';
import { AnalyticsContextType, EventPayload, ProfileViewEventAttributes } from './types';

const ONE_HOUR_IN_MS = 60 * 60 * 1000;
const PROFILE_VIEW_EVENT_NAME = 'profile_view';
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 10;

const EMPTY_CTX_VALUES = (): AnalyticsContextType => ({
  trackProfileView: () => null,
  loading: false,
  error: null,
});

export const AnalyticsContext = createContext<AnalyticsContextType>(EMPTY_CTX_VALUES());
export const useAnalytics = () => useContext(AnalyticsContext);

export const AnalyticsProvider: React.FC<{ maxBatchSize?: number; flushIntervalMs?: number }> = ({
  children,
  maxBatchSize = MAX_BATCH_SIZE,
  flushIntervalMs = FLUSH_INTERVAL_MS,
}) => {
  const [eventsBatch, setEventsBatch] = useState<EventPayload>([]);
  const [createEvents, { loading, error }] = useMutation(CreateEventsDoc);
  const { tokens } = useUserContext();

  const accountId = tokens?.accessParsed?.id;
  const accountSystemRoleCode = tokens?.accessParsed?.role?.code;

  const flushEventsBatch = useCallback(async () => {
    if (eventsBatch.length === 0) return;

    try {
      await createEvents({
        variables: { input: eventsBatch },
      });
      setEventsBatch([]);
    } catch (err) {
      console.error('Error flushing events batch:', err);
      captureExceptionEvent(
        err instanceof Error ? err : new Error('AnalyticsProvider -> Error flushing events batch'),
        {
          location: `AnalyticsProvider > flushEventsBatch`,
          errorMessage: err?.message,
          errorDescription: err?.description,
        }
      );
    }
  }, [eventsBatch, createEvents]);

  // Auto Flush at regular intervals
  useEffect(() => {
    const interval = setInterval(() => {
      flushEventsBatch().catch((e) => {
        console.error('Error in flushEventsBatch:', e);
      });
    }, flushIntervalMs);

    return () => clearInterval(interval);
  }, [flushEventsBatch, flushIntervalMs]);

  // Flush when batch size exceeds max limit
  useEffect(() => {
    if (eventsBatch.length >= maxBatchSize) {
      flushEventsBatch().catch((e) => {
        console.error('Error in flushEventsBatch:', e);
      });
    }
  }, [eventsBatch, flushEventsBatch, maxBatchSize]);

  const track = useCallback(
    ({ attributes = {}, name, uniqueEventId, dedupeIntervalMs, identity }: Partial<EventPayload[0]>) => {
      const eventPayload: EventPayload = [
        {
          name,
          identity: accountId?.toString() || identity,
          uniqueEventId,
          dedupeIntervalMs,
          attributes,
        },
      ];
      setEventsBatch((prevBatch) => [...prevBatch, ...eventPayload]);
    },
    [accountId, setEventsBatch]
  );

  const trackProfileView = useCallback(
    ({ profileId, profileAccountId, artistId }: ProfileViewEventAttributes) => {
      track({
        name: PROFILE_VIEW_EVENT_NAME,
        uniqueEventId:
          accountId && accountSystemRoleCode ? `${accountSystemRoleCode}-${accountId}-${profileId}` : undefined,
        dedupeIntervalMs: ONE_HOUR_IN_MS,
        attributes: {
          profileAccountId: profileAccountId?.toString(),
          profileId: profileId?.toString(),
          artistId: artistId?.toString(),
          viewerId: accountId?.toString(),
          viewerType: accountSystemRoleCode,
        },
      });
    },
    [track, accountId, accountSystemRoleCode]
  );

  const analyticsContextValue: AnalyticsContextType = useMemo(
    () => ({
      trackProfileView,
      loading,
      error,
    }),
    [trackProfileView, loading, error]
  );

  return <AnalyticsContext.Provider value={analyticsContextValue}>{children}</AnalyticsContext.Provider>;
};
